import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Flex, Typography, Spin, Alert, Button, message } from 'antd';
import { GetBillingAccountFinancialsQuery } from '../../../schema/generated/schema';
import {
  mockInvoices,
  mockPayments,
  mockCreditMemos,
} from '../../../mocks/BillingProfileMocks';
import { loftColors } from '../../../antd/theme';

import { useFinancialsFilterLogic } from './useFinancialsFilterLogic';
import {
  getFinancialsEmptyMessage,
  buildHistoryStatementCsv,
  triggerCsvDownload,
} from './Financials.utils';
import FinancialsSummary from './FinancialsSummary';
import FinancialsFilters from './FinancialsFilters';
import FinancialsHistoryView from './FinancialsHistoryView';
import PayNowDialog from './PayNowDialog';

const { Text } = Typography;

// ============================================================================
// Environment Helpers
// ============================================================================

const getStatsigEnv = (): string | undefined => {
  // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
  return (window as any).__env__?.STATSIG_ENV;
};

const isLocalDevelopment = (): boolean => {
  // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
  if ((window as any).__STORYBOOK_PREVIEW__) return true;
  const env = getStatsigEnv();
  return !env || env === 'development';
};

// ============================================================================
// Main Component
// ============================================================================

interface FinancialsProps {
  billingAccountId: string;
  useMockData?: boolean;
}

const Financials: React.FC<FinancialsProps> = ({
  billingAccountId,
  useMockData = false,
}) => {
  const localDev = isLocalDevelopment();

  const { loading, data, error, refetch } = useQuery(
    GetBillingAccountFinancialsQuery,
    {
      variables: {
        input: {
          billingAccountId,
        },
      },
      skip: useMockData || localDev,
    }
  );

  const liveInvoices = data?.getBillingAccount?.invoices;
  const livePayments = data?.getBillingAccount?.payments;
  const liveCreditMemos = data?.getBillingAccount?.creditMemos;

  const invoices =
    liveInvoices ?? (useMockData || localDev ? mockInvoices : []);
  const payments =
    livePayments ?? (useMockData || localDev ? mockPayments : []);
  const creditMemos =
    liveCreditMemos ?? (useMockData || localDev ? mockCreditMemos : []);

  const [isPayNowOpen, setIsPayNowOpen] = useState(false);

  const {
    searchQuery,
    statusFilter,
    dateFrom,
    dateTo,
    currentPage,
    totalPages,
    paginatedRows,
    allFilteredRows,
    totalRowCount,
    historyAmountDue,
    resultCount,
    totalCount,
    selectedRowKeys,
    allUnpaidInvoiceRows,
    handleSearchChange,
    handleStatusChange,
    handleDateFromChange,
    handleDateToChange,
    handleSelectionChange,
    setCurrentPage,
    filters,
    hasAnyBillingData,
  } = useFinancialsFilterLogic({
    invoices,
    payments,
    creditMemos,
  });

  const emptyMessage = useMemo(
    () => getFinancialsEmptyMessage(filters, hasAnyBillingData),
    [filters, hasAnyBillingData]
  );

  const handleDownloadStatement = useCallback(() => {
    const csv = buildHistoryStatementCsv(allFilteredRows);
    const dateStamp = new Date().toISOString().slice(0, 10);
    triggerCsvDownload(
      `billing-statement-${billingAccountId}-${dateStamp}.csv`,
      csv
    );
  }, [allFilteredRows, billingAccountId]);

  const handlePaymentSuccess = useCallback(() => {
    setIsPayNowOpen(false);
    handleSelectionChange([]);
    if (!useMockData && !localDev) {
      refetch();
    }
    message.success(
      'Payment submitted successfully. Your invoice status will update shortly.'
    );
  }, [handleSelectionChange, useMockData, localDev, refetch]);

  if (loading) {
    return (
      <Flex justify="center" style={{ padding: 40 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (error && !useMockData) {
    return (
      <Flex
        vertical
        align="center"
        justify="center"
        gap={12}
        style={{ padding: 40 }}
      >
        <Text style={{ color: loftColors.reds[100] }}>
          Unable to load financial data: {error.message}
        </Text>
        <Button type="primary" onClick={() => refetch()}>
          Retry
        </Button>
      </Flex>
    );
  }

  return (
    <div style={{ padding: '12px 20px 20px' }}>
      <Alert
        message="If you have questions about your billing account, please contact accounting@xometry.com"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <FinancialsSummary amountDue={historyAmountDue} />

      <FinancialsFilters
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        dateFrom={dateFrom}
        dateTo={dateTo}
        displayedCount={resultCount}
        totalCount={totalCount}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
      />

      <FinancialsHistoryView
        billingAccountId={billingAccountId}
        rows={paginatedRows}
        totalRowCount={totalRowCount}
        emptyMessage={emptyMessage}
        currentPage={currentPage}
        totalPages={totalPages}
        selectedRowKeys={selectedRowKeys ?? []}
        onSelectionChange={handleSelectionChange}
        onPageChange={setCurrentPage}
        onPayNow={() => setIsPayNowOpen(true)}
        onDownloadStatement={handleDownloadStatement}
      />

      <PayNowDialog
        open={isPayNowOpen}
        invoiceRows={(allUnpaidInvoiceRows ?? []).filter((row) =>
          selectedRowKeys.includes(row.key)
        )}
        billingAccountId={billingAccountId}
        onClose={() => setIsPayNowOpen(false)}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default Financials;
